<template>
  <base-screening-page
    page-title="Consensus Review"
    progress-label="Extractions Reviewed"
    empty-message="No more eligible extractions to review."
    none-resolved-message="No extractions have been reviewed yet."
    :breadcrumbs="breadcrumbs"
    :references="references"
    :updated-references="updatedReferences"
    :get-reference-log-item-style="getReferenceLogItemStyle"
    :loading="loading"
    :relevant-count="relevantCount"
    :remaining-reference-count="remainingReferenceCount"
    :selected-reference.sync="selectedReference"
    @go-back="goBackToReferences"
  >
    <template #middle-panel>
      <full-text-panel :reference="selectedReference" />
    </template>
    <template #right-panel="{ showingPending, selectNextReference }">
      <consensus-review-panel
        v-if="selectedReference"
        :fields="extractionFields"
        :extraction-values="extractionValues"
        :reports="reportsForSelectedReference"
        :researchers="researchers"
        :final-report="currentReport"
        :readonly="!showingPending"
        :error="errorMessage"
        :error-field-ids="errorFieldIds"
        @confirm="onConfirmValue"
        @submit="onSubmitReport(selectNextReference)"
      />
    </template>
  </base-screening-page>
</template>

<script>
import BaseScreeningPage from '@/components/Common/BaseScreeningPage';
import FullTextPanel from '@/components/FullTextReviewPage/FullTextPanel';
import ConsensusReviewPanel from '@/components/DataExtraction/ConsensusReviewPanel';
import {
  GetReviewForConsensusPage,
  GetConsensusReviewPage,
  GetCurrentResearcher,
  GetReferenceForDataExtraction,
} from '@/graphql';
import {
  Review,
  Reference,
  Researcher,
  createDataExtractionValue,
} from '@/models';
import { ReportState } from '@/enums';
import { mapActions } from 'vuex';

export default {
  name: 'DataExtractionConsensusPage',
  components: {
    BaseScreeningPage,
    FullTextPanel,
    ConsensusReviewPanel,
  },
  data: () => ({
    review: null,
    currentResearcher: null,
    topReferencesForConsensusReview: null,
    priorityReference: null,
    selectedReference: null,
    updatedItems: [],
    errorFieldIds: [],
    activeSaveTasks: {},
  }),
  apollo: {
    review: {
      query: GetReviewForConsensusPage,
      variables() {
        return {
          id: this.$route.params.reviewId,
        };
      },
      update(data) {
        const review = new Review(data.review);
        this.redirectIfNotConflictResolver(review);
        return review;
      },
    },
    currentResearcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
    topReferencesForConsensusReview: {
      query: GetConsensusReviewPage,
      variables() {
        return {
          reviewId: this.$route.params.reviewId,
          count: 20,
        };
      },
      fetchPolicy: 'cache-and-network',
    },
    priorityReference: {
      query: GetReferenceForDataExtraction,
      variables() {
        return {
          id: this.$route.query.show,
        };
      },
      update: (data) => new Reference(data.reference),
      skip() {
        return !this.$route.query.show;
      },
    },
  },
  computed: {
    breadcrumbs() {
      const review = this.review ?? {};
      return [
        {
          icon: 'home',
          text: 'Reviews',
          href: '/',
        },
        {
          text: review.title,
          href: `/review/${review.id}`,
        },
        {
          text: 'Consensus Review',
          href: `/review/${review.id}/consensus-review`,
          disabled: true,
        },
      ];
    },
    loading() {
      return (
        this.review === null ||
        this.references === null ||
        this.currentResearcher === null
      );
    },
    updatedReferences() {
      return this.updatedItems.map((item) => item.reference);
    },
    references() {
      if (!this.topReferencesForConsensusReview) return null;

      const loadedRefs = [...this.topReferencesForConsensusReview];
      const missingResolvedRefs = this.updatedReferences.filter(
        (x) => !loadedRefs.some((y) => x.id === y.id)
      );
      missingResolvedRefs.sort(Reference.sortByTime);
      loadedRefs.push(...missingResolvedRefs);

      if (this.priorityReference) {
        const priorityRefIndex = loadedRefs.findIndex(
          (x) => x.id === this.priorityReference.id
        );
        if (priorityRefIndex !== -1) {
          loadedRefs.splice(priorityRefIndex, 1);
        }
        loadedRefs.unshift(this.priorityReference);
      }

      return loadedRefs;
    },
    remainingReferenceCount() {
      const progressSummary = this.review
        ?.dataExtractionProgressSummary;
      if (!progressSummary) {
        return 0;
      }
      return progressSummary.conflictedExtractionCount;
    },
    relevantCount() {
      return this.updatedReferences.length;
    },
    currentReviewMember() {
      const reviewers = this.review.reviewMembers;
      const currentResearcher = this.currentResearcher;
      if (!reviewers || !currentResearcher) {
        return null;
      }

      return reviewers.find(
        (reviewer) => reviewer.researcher.id === currentResearcher.id
      );
    },
    currentReport() {
      const reports = this.selectedReference?.dataExtractionReports;
      const researcherId = this.currentResearcher.id;
      const validStates = [ReportState.PENDING, ReportState.ACCEPTED];
      return reports?.find(
        (report) =>
          report.reviewer?.researcher?.id === researcherId &&
          validStates.includes(report.state)
      );
    },
    proposedReports() {
      const reports = this.selectedReference?.dataExtractionReports;
      if (!reports) {
        return [];
      }

      return reports.filter(
        (report) => report.state === ReportState.PROPOSED
      );
    },
    reportsForSelectedReference() {
      return this.selectedReference?.dataExtractionReports ?? [];
    },
    researchers() {
      return this.proposedReports
        .map((report) => report.reviewer?.researcher)
        .filter(Boolean);
    },
    extractionFields() {
      return this.review?.dataExtractionFields || [];
    },
    extractionValues() {
      return this.proposedReports.flatMap((report) => report.values);
    },
    errorMessage() {
      return this.errorFieldIds.length
        ? `Please correct the highlighted fields before submitting.`
        : null;
    },
  },
  watch: {
    selectedReference() {
      this.errorFieldIds = [];
    },
  },
  methods: {
    ...mapActions({
      createReport: 'dataExtractionReport/createForReference',
      submitReport: 'dataExtractionReport/submit',
      saveValue: 'dataExtractionValue/save',
    }),
    redirectIfNotConflictResolver(review) {
      if (!review.currentUserIsDataExtractionConflictResolver()) {
        this.$router.push(`/review/${review.id}`);
      }
    },
    getReferenceLogItemStyle() {
      return {
        color: 'grey lighten-5',
        textClass: 'grey--text text--darken-2',
      };
    },
    goBackToReferences() {
      this.$router.push(`/review/${this.$route.params.reviewId}`);
    },
    async onConfirmValue({ field, updatedFields }) {
      if (this.activeSaveTasks[field]) {
        await this.activeSaveTasks[field];
      }
      const reference = this.selectedReference;
      this.errorFieldIds = [];
      const report = await this.createReportIfNotExists(reference);
      const existing = report.values.find(
        (value) => value.field.id === field.id
      );
      const valueRecord =
        existing || createDataExtractionValue({ field });
      Object.assign(valueRecord, updatedFields);
      if (!existing) {
        report.values.push(valueRecord);
      }
      this.activeSaveTasks[field] = this.saveValue({
        reportId: report.id,
        fieldId: field.id,
        dataExtractionValue: valueRecord,
      });
      try {
        await this.activeSaveTasks[field];
      } finally {
        delete this.activeSaveTasks[field];
      }
    },
    async onSubmitReport(selectNextReference) {
      const reference = this.selectedReference;
      if (!this.validateReport(reference)) {
        return;
      }

      const itemToSave = this.getCurrentReport(reference);
      try {
        this.saveItemToCache(itemToSave);
        selectNextReference(reference);
        const updatedItem = await this.submitReport({
          report: itemToSave,
          resolveConflicts: true,
        });
        this.saveItemToCache(updatedItem);
      } catch {
        this.removeFromCache(itemToSave);
      }
    },
    saveItemToCache(item) {
      const index = this.updatedItems.findIndex(
        (x) => x.reference.id === item.reference.id
      );
      if (index >= 0) {
        this.$set(this.updatedItems, index, item);
      } else {
        this.updatedItems.push(item);
      }
    },
    removeFromCache(item) {
      this.updatedItems.splice(this.updatedItems.indexOf(item), 1);
    },
    async createReportIfNotExists(reference) {
      const currentReport = this.getCurrentReport(reference);
      if (currentReport) {
        return currentReport;
      }
      return await this.createReport({
        referenceId: reference.id,
        reviewerId: this.currentReviewMember.id,
      });
    },
    validateReport(reference) {
      this.errorFieldIds = [];
      const fields = this.review.dataExtractionFields;
      const extractionValues = this.getExtractionValues(reference);
      const fieldWithValues = fields.filter((field) =>
        extractionValues.some((value) => value.field.id === field.id)
      );
      const report = this.getCurrentReport(reference);
      if (!report) {
        this.errorFieldIds = fieldWithValues.map((field) => field.id);
        return false;
      }

      // Check if all fields with values that need reviewing have been reviewed.
      const fieldsMissingValues = fieldWithValues.filter(
        (field) =>
          !report.values.some((value) => value.field.id === field.id)
      );
      if (fieldsMissingValues.length > 0) {
        this.errorFieldIds = fieldsMissingValues.map(
          (field) => field.id
        );
        return false;
      }

      return true;
    },
    getExtractionValues(reference) {
      const proposedReports = reference.dataExtractionReports.filter(
        (report) => report.state === ReportState.PROPOSED
      );
      return proposedReports.flatMap((report) => report.values);
    },
    getCurrentReport(reference) {
      const reports = reference.dataExtractionReports;
      const researcherId = this.currentResearcher.id;
      const validStates = [ReportState.PENDING, ReportState.ACCEPTED];
      return reports?.find(
        (report) =>
          report.reviewer?.researcher?.id === researcherId &&
          validStates.includes(report.state)
      );
    },
  },
};
</script>
